import React, { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { makeStyles } from '@material-ui/core/styles';
import {
	Typography,
	TextField,
	Button,
	Chip,
	Checkbox,
	Tabs,
	Tab,
	InputAdornment
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import clsx from 'clsx';
import API_Service, { axiosDefaultConfig } from '../../../Services/API_Service';
import { req_cust, req_cust_chain } from '../../../Helper/Constants';

const MAX_VISIBLE_OUTLETS = 200;

const useStyles = makeStyles((theme) => ({
	stepBlock: {
		padding: theme.spacing(1.5, 2),
		borderBottom: `1px solid ${theme.palette.divider}`
	},
	stepBlockLast: {
		borderBottom: 'none'
	},
	pickerLabel: {
		marginBottom: 0,
		marginRight: 'auto'
	},
	pillIcon: {
		fontSize: 14
	},
	stepLabel: {
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: '0.06em',
		textTransform: 'uppercase',
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(1)
	},
	countTag: {
		marginLeft: theme.spacing(1),
		fontSize: 12,
		fontWeight: 700,
		color: theme.palette.type === 'dark' ? '#e8dc00' : '#8a7d00'
	},
	chainPills: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(0.75)
	},
	chainPill: {
		height: 30,
		padding: theme.spacing(0, 1.5),
		borderRadius: 15,
		fontSize: 12.5,
		fontWeight: 600,
		textTransform: 'none',
		color: theme.palette.text.secondary,
		border: `1px solid ${theme.palette.divider}`,
		transition: theme.transitions.create(['background-color', 'color', 'border-color'], { duration: 150 })
	},
	chainPillActive: {
		color: 'rgba(0,0,0,0.86)',
		fontWeight: 700,
		borderColor: '#E8DC00',
		backgroundColor: '#FEF200',
		'&:hover': {
			backgroundColor: '#FFF53D'
		}
	},
	pickerHead: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1)
	},
	search: {
		width: 220,
		'& .MuiOutlinedInput-root': {
			borderRadius: 10
		}
	},
	outletList: {
		maxHeight: 260,
		overflowY: 'auto',
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 8
	},
	outletRow: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5),
		padding: theme.spacing(0.25, 1),
		cursor: 'pointer',
		transition: theme.transitions.create('background-color', { duration: 120 }),
		'&:hover': {
			backgroundColor: theme.palette.action.hover
		}
	},
	outletName: {
		fontSize: 13.5
	},
	outletCode: {
		fontSize: 11.5,
		color: theme.palette.text.disabled,
		fontFamily: 'monospace',
		marginLeft: theme.spacing(0.75)
	},
	capHint: {
		padding: theme.spacing(1),
		fontSize: 12,
		color: theme.palette.text.disabled,
		fontStyle: 'italic'
	},
	chipsWrap: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(0.5),
		minHeight: 28
	},
	selChip: {
		height: 22,
		fontSize: 11
	},
	emptyChips: {
		fontSize: 12.5,
		color: theme.palette.text.disabled,
		fontStyle: 'italic'
	},
	sectionError: {
		color: theme.palette.error.main,
		fontSize: 12.5,
		padding: theme.spacing(0, 2, 1.5)
	}
}));

export default function OutletSection({ form, onFormChange, client, language, errors }) {
	const classes = useStyles();
	const { enqueueSnackbar } = useSnackbar();
	const [chains, setChains] = useState([]);
	const [outlets, setOutlets] = useState([]);
	const [selectedChains, setSelectedChains] = useState([]);
	const [activeTab, setActiveTab] = useState('');
	const [search, setSearch] = useState('');

	const sectionErrors = (errors || []).filter((err) => err.section === 3);

	useEffect(() => {
		API_Service.get(req_cust_chain, axiosDefaultConfig(client))
			.then(({ data }) => {
				const { result, extraData } = data;
				if (parseInt(result) === 1 && Array.isArray(extraData)) {
					setChains(extraData.filter((c) => parseInt(c.cust_chain_status) === 1));
				}
			})
			.catch(() => enqueueSnackbar(language.no_internet || 'Network error', { variant: 'error' }));
		API_Service.get(req_cust, axiosDefaultConfig(client))
			.then(({ data }) => {
				const { result, extraData } = data;
				if (parseInt(result) === 1 && extraData && Array.isArray(extraData.customer)) {
					setOutlets(extraData.customer);
				}
			})
			.catch(() => enqueueSnackbar(language.no_internet || 'Network error', { variant: 'error' }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [client]);

	useEffect(() => {
		if (form.id && form.outlets.length > 0) {
			const chainIds = [...new Set(form.outlets.map((o) => String(o.cust_chain_id)))];
			setSelectedChains(chainIds);
			setActiveTab(chainIds[0]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form.id]);

	const chainName = (chainId) => {
		const chain = chains.find((c) => String(c.cust_chain_id) === String(chainId));
		if (chain) {
			return chain.cust_chain_name;
		}
		const fromForm = form.outlets.find((o) => String(o.cust_chain_id) === String(chainId));
		return fromForm && fromForm.cust_chain_name ? fromForm.cust_chain_name : `#${chainId}`;
	};

	const toggleChain = (chainId) => {
		const key = String(chainId);
		if (selectedChains.includes(key)) {
			const next = selectedChains.filter((c) => c !== key);
			setSelectedChains(next);
			if (activeTab === key) {
				setActiveTab(next.length > 0 ? next[0] : '');
			}
			onFormChange((prev) => ({
				...prev,
				outlets: prev.outlets.filter((o) => String(o.cust_chain_id) !== key)
			}));
		} else {
			setSelectedChains([...selectedChains, key]);
			setActiveTab(key);
		}
	};

	const visibleOutlets = useMemo(() => {
		if (!activeTab) {
			return [];
		}
		const q = search.trim().toLowerCase();
		return outlets.filter((o) => {
			if (String(o.cust_chain) !== activeTab) {
				return false;
			}
			if (!q) {
				return true;
			}
			const name = (o.cust_company_name || '').toLowerCase();
			const code = (o.cust_code || '').toLowerCase();
			return name.indexOf(q) > -1 || code.indexOf(q) > -1;
		});
	}, [outlets, activeTab, search]);

	const isPicked = (custCode) => form.outlets.some((o) => o.cust_code === custCode);

	const toggleOutlet = (outlet) => {
		onFormChange((prev) => {
			if (prev.outlets.some((o) => o.cust_code === outlet.cust_code)) {
				return { ...prev, outlets: prev.outlets.filter((o) => o.cust_code !== outlet.cust_code) };
			}
			return {
				...prev,
				outlets: [...prev.outlets, {
					id: '',
					cust_code: outlet.cust_code,
					cust_company_name: outlet.cust_company_name || outlet.cust_code,
					cust_chain_id: String(outlet.cust_chain),
					cust_chain_name: outlet.cust_chain_name || chainName(outlet.cust_chain)
				}]
			};
		});
	};

	const selectAllVisible = () => {
		const slice = visibleOutlets.slice(0, MAX_VISIBLE_OUTLETS);
		onFormChange((prev) => {
			const existing = new Set(prev.outlets.map((o) => o.cust_code));
			const additions = slice
				.filter((o) => !existing.has(o.cust_code))
				.map((o) => ({
					id: '',
					cust_code: o.cust_code,
					cust_company_name: o.cust_company_name || o.cust_code,
					cust_chain_id: String(o.cust_chain),
					cust_chain_name: o.cust_chain_name || chainName(o.cust_chain)
				}));
			return { ...prev, outlets: [...prev.outlets, ...additions] };
		});
	};

	const clearAllVisible = () => {
		const codes = new Set(visibleOutlets.slice(0, MAX_VISIBLE_OUTLETS).map((o) => o.cust_code));
		onFormChange((prev) => ({
			...prev,
			outlets: prev.outlets.filter((o) => !codes.has(o.cust_code))
		}));
	};

	const removeChip = (custCode) => () => {
		onFormChange((prev) => ({
			...prev,
			outlets: prev.outlets.filter((o) => o.cust_code !== custCode)
		}));
	};

	return (
		<div>
			<div className={classes.stepBlock}>
				<Typography className={classes.stepLabel}>
					{language.select_chains}
					<span className={classes.countTag}>{form.outlets.length} {language.outlets_selected_count}</span>
				</Typography>
				<div className={classes.chainPills}>
					{chains.map((chain) => {
						const key = String(chain.cust_chain_id);
						const active = selectedChains.includes(key);
						return (
							<Button
								key={key}
								disableElevation
								className={clsx(classes.chainPill, active && classes.chainPillActive)}
								startIcon={active ? <CheckCircleIcon className={classes.pillIcon} /> : <RadioButtonUncheckedIcon className={classes.pillIcon} />}
								onClick={() => toggleChain(key)}>
								{chain.cust_chain_name}
							</Button>
						);
					})}
				</div>
			</div>

			{selectedChains.length > 0 && (
				<div className={classes.stepBlock}>
					<div className={classes.pickerHead}>
						<Typography className={clsx(classes.stepLabel, classes.pickerLabel)}>
							{language.pick_outlets}
						</Typography>
						<TextField
							className={classes.search}
							variant="outlined"
							size="small"
							placeholder={language.search}
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon fontSize="small" />
									</InputAdornment>
								)
							}}
						/>
						<Button size="small" variant="outlined" onClick={selectAllVisible}>
							{language.select_all || 'Select all'}
						</Button>
						<Button size="small" variant="outlined" color="secondary" onClick={clearAllVisible}>
							{language.clear_all}
						</Button>
					</div>
					<Tabs
						value={activeTab}
						onChange={(e, v) => setActiveTab(v)}
						variant="scrollable"
						scrollButtons="auto"
						indicatorColor="primary">
						{selectedChains.map((chainId) => (
							<Tab key={chainId} value={chainId} label={chainName(chainId)} />
						))}
					</Tabs>
					<div className={classes.outletList}>
						{visibleOutlets.slice(0, MAX_VISIBLE_OUTLETS).map((outlet) => (
							<div key={outlet.cust_code} className={classes.outletRow} onClick={() => toggleOutlet(outlet)}>
								<Checkbox size="small" color="primary" checked={isPicked(outlet.cust_code)} />
								<Typography className={classes.outletName}>
									{outlet.cust_company_name}
									<span className={classes.outletCode}>{outlet.cust_code}</span>
								</Typography>
							</div>
						))}
						{visibleOutlets.length > MAX_VISIBLE_OUTLETS && (
							<Typography className={classes.capHint}>
								+{visibleOutlets.length - MAX_VISIBLE_OUTLETS}… {language.search}
							</Typography>
						)}
						{visibleOutlets.length === 0 && (
							<Typography className={classes.capHint}>{language.no_data}</Typography>
						)}
					</div>
				</div>
			)}

			<div className={clsx(classes.stepBlock, classes.stepBlockLast)}>
				<Typography className={classes.stepLabel}>{language.selected_outlets}</Typography>
				<div className={classes.chipsWrap}>
					{form.outlets.length === 0 ? (
						<Typography className={classes.emptyChips}>{language.no_outlets_selected}</Typography>
					) : form.outlets.map((o) => (
						<Chip
							key={o.cust_code}
							size="small"
							className={classes.selChip}
							label={o.cust_company_name}
							onDelete={removeChip(o.cust_code)}
						/>
					))}
				</div>
			</div>
			{sectionErrors.map((err) => (
				<Typography key={err.message} className={classes.sectionError}>{err.message}</Typography>
			))}
		</div>
	);
}
